import { periodFromParams, periodToParams, type PeriodValue } from '@elva/ui';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The report period, kept in the URL (spec 41 §2). `?range=24h|7d|30d|60d|all`
 * or `?from=…&to=…`; nothing means 30 days. Reloading keeps the choice.
 *
 * Returns the current value and a setter the PeriodSelector drives. Setting a
 * value replaces the range/from/to params and leaves every other param alone.
 */
export function usePeriodRange(): [PeriodValue, (value: PeriodValue) => void] {
  const [params, setParams] = useSearchParams();
  const value = periodFromParams(params);

  const setValue = useCallback(
    (next: PeriodValue) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete('range');
          p.delete('from');
          p.delete('to');
          for (const [k, v] of Object.entries(periodToParams(next))) p.set(k, v);
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return [value, setValue];
}
